import { useState, useEffect } from 'react';
import { getSignedPhotoUrl } from '../services/supabase/storage';

/**
 * Custom hook to fetch and cache signed photo URLs
 * Automatically refreshes URLs before they expire
 * @param {string} path - Storage path of the photo
 * @param {number} expiresIn - URL validity in seconds (default: 3600 = 1h)
 * @returns {Object} - { url, loading, error, refresh }
 */
export function usePhotoUrl(path, expiresIn = 3600) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUrl = async () => {
    if (!path) {
      setUrl(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await getSignedPhotoUrl(path, expiresIn);

      if (result.error) {
        // Don't log 404 errors (file doesn't exist yet) - this is expected for new users
        if (!result.error.includes('not found') && !result.error.includes('404')) {
          console.error('Error fetching photo URL:', result.error);
        }
        setUrl(null);
        setError(null); // Don't set error for missing files
      } else {
        setUrl(result.url);
      }
    } catch (err) {
      console.error('Error fetching photo URL:', err);
      setError(err.message);
      setUrl(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUrl();

    // Refresh URL before it expires (90% of expiry time)
    const refreshInterval = (expiresIn * 1000 * 0.9);
    const interval = setInterval(fetchUrl, refreshInterval);

    return () => clearInterval(interval);
  }, [path, expiresIn]);

  return {
    url,
    loading,
    error,
    refresh: fetchUrl,
  };
}

/**
 * Hook to fetch multiple photo URLs in batch
 * @param {string[]} paths - Array of storage paths
 * @param {number} expiresIn - URL validity in seconds
 * @returns {Object} - { urls, loading, error }
 */
export function usePhotoUrls(paths = [], expiresIn = 3600) {
  const [urls, setUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUrls = async () => {
      if (!paths || paths.length === 0) {
        setUrls({});
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const urlMap = {};

        // Fetch all URLs in parallel
        await Promise.all(
          paths.map(async (path) => {
            const result = await getSignedPhotoUrl(path, expiresIn);
            if (!result.error) {
              urlMap[path] = result.url;
            }
          })
        );

        setUrls(urlMap);
      } catch (err) {
        console.error('Error fetching photo URLs:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUrls();

    // Refresh URLs before they expire
    const refreshInterval = (expiresIn * 1000 * 0.9);
    const interval = setInterval(fetchUrls, refreshInterval);

    return () => clearInterval(interval);
  }, [JSON.stringify(paths), expiresIn]);

  return {
    urls,
    loading,
    error,
  };
}
